import type { Locale } from "../content/types";

export const legalForms = ["aktiebolag", "enskild_firma", "handelsbolag", "forening", "other", "unknown"] as const;
export const vatPeriods = ["monthly", "quarterly", "yearly", "none", "unknown"] as const;
export const bankSources = ["bank_file", "revolut", "manual_upload", "future_bank_integration", "unknown"] as const;
export const deliveryPreferences = ["self_orchestrated_agent", "human_assisted", "hybrid"] as const;
export const reportingNeeds = [
  "vat_report",
  "balance_sheet",
  "profit_loss",
  "general_ledger",
  "journal_list",
  "account_balances",
  "reports_register",
  "annual_artifacts",
  "management_package"
] as const;

export type LegalForm = (typeof legalForms)[number];
export type VatPeriod = (typeof vatPeriods)[number];
export type BankSource = (typeof bankSources)[number];
export type DeliveryPreference = (typeof deliveryPreferences)[number];
export type ReportingNeed = (typeof reportingNeeds)[number];

export type IntakeFormField =
  | "companyName"
  | "contactName"
  | "contactEmail"
  | "contactPhone"
  | "registrationNumber"
  | "vatNumber"
  | "legalForm"
  | "businessDescription"
  | "monthlyTransactionCount"
  | "monthlySalesInvoiceCount"
  | "monthlySupplierInvoiceCount"
  | "monthlyReceiptCount"
  | "payrollEmployeeCount"
  | "bankAccountCount"
  | "bankSources"
  | "vatPeriod"
  | "usesSieMigration"
  | "needsCleanup"
  | "needsBokslut"
  | "needsFixedAssets"
  | "fixedAssetCount"
  | "needsApArLedgers"
  | "reportingNeeds"
  | "deliveryPreference"
  | "consent";

export type ValidationCode =
  | "required"
  | "identifier_required"
  | "identifier_format"
  | "email_format"
  | "phone_format"
  | "length"
  | "integer"
  | "range"
  | "enum"
  | "duplicate"
  | "missing_consent";

export type ValidationIssue = {
  field: IntakeFormField;
  code: ValidationCode;
};

export type IntakeFormValues = {
  companyName: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  registrationNumber: string;
  vatNumber: string;
  legalForm: LegalForm;
  businessDescription: string;
  monthlyTransactionCount: string;
  monthlySalesInvoiceCount: string;
  monthlySupplierInvoiceCount: string;
  monthlyReceiptCount: string;
  payrollEmployeeCount: string;
  bankAccountCount: string;
  bankSources: BankSource[];
  vatPeriod: VatPeriod;
  usesSieMigration: boolean;
  needsCleanup: boolean;
  needsBokslut: boolean;
  needsFixedAssets: boolean;
  fixedAssetCount: string;
  needsApArLedgers: boolean;
  reportingNeeds: ReportingNeed[];
  deliveryPreference: DeliveryPreference;
  privacyAcknowledged: boolean;
  termsAccepted: boolean;
  marketingOptIn: boolean;
};

export type PublicIntakeRequest = {
  locale: Locale;
  customer: {
    company_name: string;
    contact_name: string;
    business_email: string;
    contact_phone?: string | null;
    registration_number?: string | null;
    vat_number?: string | null;
    legal_form: LegalForm;
    country_code?: string;
  };
  accounting_profile: {
    business_description: string;
    monthly_transaction_count: number;
    monthly_sales_invoice_count?: number;
    monthly_supplier_invoice_count?: number;
    monthly_receipt_count?: number;
    payroll_employee_count?: number;
    bank_account_count?: number;
    bank_sources?: BankSource[];
    vat_period: VatPeriod;
    uses_sie_migration?: boolean;
    needs_cleanup?: boolean;
    needs_bokslut?: boolean;
    needs_fixed_assets?: boolean;
    fixed_asset_count?: number;
    needs_ap_ar_ledgers?: boolean;
    reporting_needs?: ReportingNeed[];
  };
  delivery_preference: DeliveryPreference;
  source: {
    source: "dynamicbooks_public_site";
    campaign?: string | null;
    referrer?: string | null;
    page_path?: string | null;
  };
  consent: {
    privacy_acknowledged: true;
    terms_accepted: true;
    marketing_opt_in?: boolean;
  };
  attachments?: [];
};

export type ServicePackage = "start" | "growth" | "operator" | "migration_cleanup" | "custom";
export type EstimateConfidence = "low" | "medium" | "high";
export type SuggestedSubscriptionTier =
  | "DynamicBooks Basic"
  | "DynamicBooks Business"
  | "DynamicBooks Premium"
  | "Custom onboarding";

export type EstimatePriceBreakdown = {
  net_sek: number;
  vat_sek: number;
  gross_sek: number;
};

export type ExactServiceEstimatePricing = {
  currency: "SEK";
  vat_rate_percent: number;
  monthly_service_fee: EstimatePriceBreakdown;
  one_time_setup_fee: EstimatePriceBreakdown;
  subtotal_before_discount: EstimatePriceBreakdown;
  discount: EstimatePriceBreakdown & {
    percent: number;
    amount_sek: number;
    applies_to: "monthly_service_fee_plus_setup_fee";
  };
  final_total: EstimatePriceBreakdown;
  included_software_tier: {
    name: SuggestedSubscriptionTier;
    monthly_net_sek: number;
    monthly_vat_sek: number;
    monthly_gross_sek: number;
    included_in_monthly_service_fee: true;
  };
};

export type EstimateCoefficientConfigSnapshot = {
  version: string;
  source: "DEFAULT_ESTIMATE_RULE_CONFIG" | "crm_pricing_config";
  service_package: ServicePackage;
  delivery_path: DeliveryPreference;
  vat_rate_percent: number;
  discount_percent: number;
  base_monthly_service_gross_sek: number;
  base_setup_gross_sek: number;
  monthly_service_before_delivery_gross_sek?: number;
  monthly_service_after_delivery_before_software_gross_sek?: number;
  setup_before_delivery_gross_sek?: number;
  setup_after_delivery_gross_sek?: number;
  included_software_tier: SuggestedSubscriptionTier;
  included_software_monthly_gross_sek: number;
  included_subscription_mapping?: Record<ServicePackage, SuggestedSubscriptionTier>;
  included_subscription_monthly_gross_sek?: Record<SuggestedSubscriptionTier, number>;
  service_coefficients?: Record<
    string,
    {
      unit: string;
      monthly_gross_sek_per_unit: number;
      setup_gross_sek_per_unit: number;
      monthly_min_units: number;
      monthly_max_units: number | null;
      enabled_by_default: boolean;
    }
  >;
  selected_service_coefficients?: Array<{
    key: string;
    unit: string;
    units: number;
    monthly_gross_sek_per_unit: number;
    setup_gross_sek_per_unit: number;
    monthly_gross_sek: number;
    setup_gross_sek: number;
  }>;
  band_anchor: {
    monthly_position: number;
    setup_position: number;
  };
  delivery_multipliers: {
    monthly: number;
    setup: number;
  };
};

export type DynamicBooksEstimate = {
  model: "non_binding_range";
  pricing_model?: "exact_service_estimate";
  contract_version?: "dynamicbooks.exact_service_estimate.v1";
  currency: "SEK";
  service_package: ServicePackage;
  delivery_path: DeliveryPreference;
  price_band: {
    min_monthly_sek: number;
    max_monthly_sek: number;
    setup_min_sek: number;
    setup_max_sek: number;
  };
  agent_credits: {
    expected_monthly_credits: number;
    suggested_initial_credit_pack: number | "custom";
    credit_assumptions: string[];
  };
  suggested_subscription_tier: SuggestedSubscriptionTier;
  exact_pricing?: ExactServiceEstimatePricing;
  coefficient_config_snapshot?: EstimateCoefficientConfigSnapshot;
  legacy_compatibility?: {
    price_band_adapter: "retained_range";
    unknown_legacy_keys_preserved: true;
  };
  confidence: EstimateConfidence;
  assumptions: string[];
  included_workstreams: string[];
  not_included: string[];
  next_steps: string[];
  disclaimer: {
    sv: string;
    en: string;
  };
};

export type IntakeStatus =
  | "received"
  | "estimate_queued"
  | "estimate_running"
  | "estimate_ready"
  | "awaiting_customer"
  | "closed";

export type BidStatus = "queued" | "generated" | "sent" | "send_failed";

export type PublicBidDeliverySummary = {
  status: BidStatus;
  message: string;
  updated_at: string;
  artifact?: {
    file_name: string;
    mime_type: "application/pdf" | "text/html";
  };
};

export type PublicIntakeResponseData = {
  case_public_id: string;
  case_display_id: string;
  request_public_id: string;
  quote_public_id: string;
  bid_display_id: string;
  quote_run_public_id: string;
  viewer_token: string;
  case_path: string;
  status_path: string;
  stream_path: string;
  created_at: string;
  expires_at: string | null;
  status: IntakeStatus;
  bid_status: BidStatus;
  bid_delivery: PublicBidDeliverySummary;
  estimate: DynamicBooksEstimate;
  accepted_attachments: Array<{
    attachment_public_id: string;
    filename: string;
    content_type: string;
    byte_size: number;
    kind: "sie_file" | "bank_statement" | "sample_invoice" | "supporting_material";
    sha256: string;
  }>;
};

export type PublicIntakeResponse = {
  ok: true;
  data: PublicIntakeResponseData;
};

export type ValidateIntakeOptions = {
  locale: Locale;
  pagePath?: string | null;
};

export type ValidateIntakeResult =
  | {
      ok: true;
      request: PublicIntakeRequest;
    }
  | {
      ok: false;
      issues: ValidationIssue[];
    };

export function getDefaultIntakeFormValues(): IntakeFormValues {
  return {
    companyName: "",
    contactName: "",
    contactEmail: "",
    contactPhone: "",
    registrationNumber: "",
    vatNumber: "",
    legalForm: "unknown",
    businessDescription: "",
    monthlyTransactionCount: "",
    monthlySalesInvoiceCount: "0",
    monthlySupplierInvoiceCount: "0",
    monthlyReceiptCount: "0",
    payrollEmployeeCount: "0",
    bankAccountCount: "0",
    bankSources: [],
    vatPeriod: "unknown",
    usesSieMigration: false,
    needsCleanup: false,
    needsBokslut: false,
    needsFixedAssets: false,
    fixedAssetCount: "0",
    needsApArLedgers: false,
    reportingNeeds: [],
    deliveryPreference: "hybrid",
    privacyAcknowledged: false,
    termsAccepted: false,
    marketingOptIn: false
  };
}

export function validateIntakeForm(values: IntakeFormValues, options: ValidateIntakeOptions): ValidateIntakeResult {
  const issues: ValidationIssue[] = [];
  const companyName = validateText(values.companyName, "companyName", issues, 2, 160);
  const contactName = validateText(values.contactName, "contactName", issues, 2, 120);
  const email = validateEmail(values.contactEmail, issues);
  const contactPhone = validateOptionalPhone(values.contactPhone, issues);
  const registrationNumber = normalizeIdentifier(values.registrationNumber, "registrationNumber", issues);
  const vatNumber = normalizeIdentifier(values.vatNumber, "vatNumber", issues);
  const legalForm = validateEnum(values.legalForm, legalForms, "legalForm", issues);
  const businessDescription = validateText(values.businessDescription, "businessDescription", issues, 20, 2000);
  const monthlyTransactionCount = validateInteger(
    values.monthlyTransactionCount,
    "monthlyTransactionCount",
    issues,
    0,
    100000,
    true
  );
  const monthlySalesInvoiceCount = validateInteger(
    values.monthlySalesInvoiceCount,
    "monthlySalesInvoiceCount",
    issues,
    0,
    100000,
    false
  );
  const monthlySupplierInvoiceCount = validateInteger(
    values.monthlySupplierInvoiceCount,
    "monthlySupplierInvoiceCount",
    issues,
    0,
    100000,
    false
  );
  const monthlyReceiptCount = validateInteger(
    values.monthlyReceiptCount,
    "monthlyReceiptCount",
    issues,
    0,
    100000,
    false
  );
  const payrollEmployeeCount = validateInteger(values.payrollEmployeeCount, "payrollEmployeeCount", issues, 0, 10000, false);
  const bankAccountCount = validateInteger(values.bankAccountCount, "bankAccountCount", issues, 0, 1000, false);
  const bankSourceValues = validateUniqueEnums(values.bankSources, bankSources, "bankSources", issues, 10);
  const vatPeriod = validateEnum(values.vatPeriod, vatPeriods, "vatPeriod", issues);
  const fixedAssetCount = validateInteger(values.fixedAssetCount, "fixedAssetCount", issues, 0, 100000, false);
  const reportingNeedValues = validateUniqueEnums(values.reportingNeeds, reportingNeeds, "reportingNeeds", issues, 10);
  const deliveryPreference = validateEnum(values.deliveryPreference, deliveryPreferences, "deliveryPreference", issues);

  if (!registrationNumber && !vatNumber) {
    issues.push({ field: "registrationNumber", code: "identifier_required" });
  }

  if (!values.privacyAcknowledged || !values.termsAccepted) {
    issues.push({ field: "consent", code: "missing_consent" });
  }

  if (issues.length > 0) {
    return { ok: false, issues: dedupeIssues(issues) };
  }

  return {
    ok: true,
    request: {
      locale: options.locale,
      customer: {
        company_name: companyName,
        contact_name: contactName,
        business_email: email,
        contact_phone: contactPhone,
        registration_number: registrationNumber,
        vat_number: vatNumber,
        legal_form: legalForm,
        country_code: "SE"
      },
      accounting_profile: {
        business_description: businessDescription,
        monthly_transaction_count: monthlyTransactionCount,
        monthly_sales_invoice_count: monthlySalesInvoiceCount,
        monthly_supplier_invoice_count: monthlySupplierInvoiceCount,
        monthly_receipt_count: monthlyReceiptCount,
        payroll_employee_count: payrollEmployeeCount,
        bank_account_count: bankAccountCount,
        bank_sources: bankSourceValues,
        vat_period: vatPeriod,
        uses_sie_migration: values.usesSieMigration,
        needs_cleanup: values.needsCleanup,
        needs_bokslut: values.needsBokslut,
        needs_fixed_assets: values.needsFixedAssets || fixedAssetCount > 0,
        fixed_asset_count: fixedAssetCount,
        needs_ap_ar_ledgers: values.needsApArLedgers,
        reporting_needs: reportingNeedValues
      },
      delivery_preference: deliveryPreference,
      source: {
        source: "dynamicbooks_public_site",
        campaign: null,
        referrer: null,
        page_path: normalizePagePath(options.pagePath)
      },
      consent: {
        privacy_acknowledged: true,
        terms_accepted: true,
        marketing_opt_in: values.marketingOptIn
      },
      attachments: []
    }
  };
}

function trimValue(value: string): string {
  return value.trim();
}

function validateText(
  value: string,
  field: IntakeFormField,
  issues: ValidationIssue[],
  minLength: number,
  maxLength: number
): string {
  const trimmed = trimValue(value);
  if (trimmed.length === 0) {
    issues.push({ field, code: "required" });
    return "";
  }
  if (trimmed.length < minLength || trimmed.length > maxLength) {
    issues.push({ field, code: "length" });
  }
  return trimmed;
}

function validateEmail(value: string, issues: ValidationIssue[]): string {
  const email = trimValue(value).toLowerCase();
  if (email.length === 0) {
    issues.push({ field: "contactEmail", code: "required" });
    return "";
  }
  if (email.length > 254 || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    issues.push({ field: "contactEmail", code: "email_format" });
  }
  return email;
}

function validateOptionalPhone(value: string, issues: ValidationIssue[]): string | null {
  const phone = trimValue(value);
  if (phone.length === 0) {
    return null;
  }
  if (phone.length > 40 || !/^[+0-9 ()-]+$/.test(phone)) {
    issues.push({ field: "contactPhone", code: "phone_format" });
  }
  return phone;
}

function normalizeIdentifier(value: string, field: "registrationNumber" | "vatNumber", issues: ValidationIssue[]): string | null {
  const raw = trimValue(value);
  if (raw.length === 0) {
    return null;
  }
  if (raw.length > 32) {
    issues.push({ field, code: "length" });
    return null;
  }
  const normalized = raw.replace(/[^a-z0-9]/gi, "").toUpperCase();
  if (normalized.length < 6 || normalized.length > 20) {
    issues.push({ field, code: "identifier_format" });
  }
  return normalized;
}

function validateInteger(
  value: string,
  field: IntakeFormField,
  issues: ValidationIssue[],
  minValue: number,
  maxValue: number,
  required: boolean
): number {
  const trimmed = trimValue(value);
  if (trimmed.length === 0) {
    if (required) {
      issues.push({ field, code: "required" });
    }
    return 0;
  }
  if (!/^\d+$/.test(trimmed)) {
    issues.push({ field, code: "integer" });
    return 0;
  }
  const parsed = Number(trimmed);
  if (!Number.isSafeInteger(parsed) || parsed < minValue || parsed > maxValue) {
    issues.push({ field, code: "range" });
    return parsed;
  }
  return parsed;
}

function validateEnum<const T extends readonly string[]>(
  value: string,
  allowedValues: T,
  field: IntakeFormField,
  issues: ValidationIssue[]
): T[number] {
  if (allowedValues.includes(value)) {
    return value as T[number];
  }
  issues.push({ field, code: "enum" });
  return allowedValues[allowedValues.length - 1] as T[number];
}

function validateUniqueEnums<const T extends readonly string[]>(
  values: string[],
  allowedValues: T,
  field: IntakeFormField,
  issues: ValidationIssue[],
  maxItems: number
): T[number][] {
  if (values.length > maxItems) {
    issues.push({ field, code: "range" });
  }

  const seen = new Set<string>();
  const allowed = new Set<string>(allowedValues);
  const normalized: T[number][] = [];

  for (const value of values) {
    if (!allowed.has(value)) {
      issues.push({ field, code: "enum" });
      continue;
    }
    if (seen.has(value)) {
      issues.push({ field, code: "duplicate" });
      continue;
    }
    seen.add(value);
    normalized.push(value as T[number]);
  }

  return normalized;
}

function normalizePagePath(value: string | null | undefined): string | null {
  const path = trimValue(value ?? "");
  if (path.length === 0 || path.length > 256 || !path.startsWith("/") || hasControlChars(path)) {
    return null;
  }
  return path;
}

function hasControlChars(value: string): boolean {
  return /[\u0000-\u001f\u007f]/.test(value);
}

function dedupeIssues(issues: ValidationIssue[]): ValidationIssue[] {
  const seen = new Set<string>();
  return issues.filter((issue) => {
    const key = `${issue.field}:${issue.code}`;
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
}
